import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { message } from 'antd';

import { apiReducerBuilder, setInStorage } from 'utils';
import i18n from 'utils/i18n';
import { IS_SOLD_INVESTMENT, RequestStatus } from 'utils/constants';

import { IMyInvestments, RequestStatusTypes } from 'models';

import {
	downloadInvestmentAgreement,
	downloadCurrentInvestments,
	downloadFinishedInvestments,
	getMyCurrentInvestments,
	getMyFinishedInvestments,
	getSaleProject,
	saleInvestment,
	cancelSaleInvestment,
	downloadAssigmentAgreement,
} from './actions';
import { logout } from '../authorization/actions';
import { deleteAccountConfirm } from '../userSettings/actions';

import { Pager, SaleProject } from 'models';

interface MyInvestments {
	pager: Pager;
	saleProject: SaleProject;
	isSold: boolean;
	sellingPrice: string;
	myInvestments: IMyInvestments[];
	status: RequestStatusTypes;
	statusAgreementDownload: RequestStatusTypes;
}

const initialState: MyInvestments = {
	myInvestments: [],
	pager: {} as Pager,
	saleProject: {} as SaleProject,
	isSold: false,
	sellingPrice: '',
	status: '' as RequestStatusTypes,
	statusAgreementDownload: '' as RequestStatusTypes,
};

const myInvestmentsSlice = createSlice({
	name: 'myInvestments',
	initialState,
	reducers: { resetMyInvestments: () => initialState },
	extraReducers: (builder) => {
		apiReducerBuilder(builder, downloadCurrentInvestments, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		});

		apiReducerBuilder(builder, getSaleProject, (state, action) => {
			const { payload, meta } = action;
			state.saleProject = payload;
			state.status = meta.requestStatus;
		});

		apiReducerBuilder(builder, saleInvestment, (state, action) => {
			const { meta, payload } = action;
			state.isSold = true;
			state.sellingPrice = payload.amount;
			state.status = meta.requestStatus;

			setInStorage(IS_SOLD_INVESTMENT, 'true');
		});

		apiReducerBuilder(builder, downloadFinishedInvestments, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		});

		builder.addCase(cancelSaleInvestment.rejected, (state, action) => {
			state.status = RequestStatus.FULFILLED;
		});

		builder.addMatcher(
			isAnyOf(downloadInvestmentAgreement.rejected, downloadAssigmentAgreement.rejected),
			(state) => {
				state.statusAgreementDownload = RequestStatus.FULFILLED;
				const text: string = i18n.t('agreementNotFoundText');
				message.warn(text);
			},
		);

		builder.addMatcher(isAnyOf(logout, deleteAccountConfirm.fulfilled), (state) => {
			return initialState;
		});

		builder.addMatcher(
			isAnyOf(
				downloadInvestmentAgreement.pending,
				downloadInvestmentAgreement.fulfilled,
				downloadAssigmentAgreement.fulfilled,
				downloadAssigmentAgreement.pending,
			),
			(state, action) => {
				const { meta } = action;
				state.statusAgreementDownload = meta.requestStatus;
			},
		);

		builder.addMatcher(
			isAnyOf(
				getMyCurrentInvestments.pending,
				getMyFinishedInvestments.pending,
				cancelSaleInvestment.pending,
				cancelSaleInvestment.fulfilled,
			),
			(state, action) => {
				const { meta } = action;
				state.status = meta.requestStatus;
			},
		);

		builder.addMatcher(
			isAnyOf(getMyCurrentInvestments.fulfilled, getMyFinishedInvestments.fulfilled),
			(state, action) => {
				const { payload, meta } = action;
				state.status = meta.requestStatus;

				state.myInvestments = payload.items.map((item: { id: number }) => {
					return { ...item, key: item.id };
				});

				state.pager = payload.pager;
			},
		);

		builder.addMatcher(
			isAnyOf(getMyCurrentInvestments.rejected, getMyFinishedInvestments.rejected),
			(state, action) => {
				const { meta, error } = action;
				state.status = meta.requestStatus;
				message.warn(error.message);
				state.myInvestments = [];
				state.pager = {} as Pager;
			},
		);
	},
});

export const { resetMyInvestments } = myInvestmentsSlice.actions;

export default myInvestmentsSlice.reducer;
